import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { PatientList } from '@/components/forms/PatientList';
import { PatientForm } from '@/components/forms/PatientForm';
import { SearchPatientForTakeover } from '@/components/patients/SearchPatientForTakeover';
import { AddPatientWizard } from '@/components/forms/AddPatientWizard';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { UserCheck, Plus, Maximize2, Minimize2, UserPlus, Edit, X, Download, Baby, Trash2 } from 'lucide-react';
import { usePatients } from '@/hooks/usePatients';
import { cn } from '@/lib/utils';
import { Patient, PatientFilters } from '@/types/patient.types';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { UserLevel } from '@/types/auth.types';
import { patientService } from '@/services/patientService';
import apiClient from '@/services/api';

export const PrematurePatientsPage: React.FC = () => {
    const { toast } = useToast();
    const { utilizator } = useAuth();
    const [isTakeoverOpen, setIsTakeoverOpen] = useState(false);
    const [takeoverCnp, setTakeoverCnp] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<any>(null);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isWizardMaximized, setIsWizardMaximized] = useState(false);
    const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const userRole = useMemo(() => {
        const levelId = utilizator?.user_level?.id || utilizator?.user_level_id;
        if (levelId === UserLevel.ADMINISTRATOR) return 'admin';
        if (levelId === UserLevel.MATERNITY) return 'maternity';
        return 'hospital';
    }, [utilizator]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    // Initialize with premature patient filter
    const [filters, setFilters] = useState<PatientFilters>({
        is_premature: true
    });

    const [isExporting, setIsExporting] = useState(false);

    const queryFilters = useMemo(() => {
        return {
            page: currentPage,
            limit: pageSize,
            ...filters,
            is_premature: true, // Enforce this filter to show only premature patients
        };
    }, [currentPage, pageSize, filters]);

    const { data: patients, isLoading, error, refetch } = usePatients(queryFilters);

    // Listen for open-new-premature-patient-modal event (from dashboard quick action)
    useEffect(() => {
        const handleOpenModal = () => {
            setIsWizardOpen(true);
        };

        window.addEventListener('open-new-premature-patient-modal', handleOpenModal);
        return () => {
            window.removeEventListener('open-new-premature-patient-modal', handleOpenModal);
        };
    }, []);

    const handleCreatePatient = () => {
        setSelectedPatient(null);
        setIsWizardOpen(true);
    };

    const handleDuplicatePatient = (cnp: string) => {
        setIsWizardOpen(false);
        setTakeoverCnp(cnp);
        setIsTakeoverOpen(true);
    };

    const handleEditPatient = (patient: any) => {
        setSelectedPatient(patient);
        setIsFormOpen(true);
    };

    const handleDeletePatient = (patient: Patient) => {
        setPatientToDelete(patient);
    };

    const handleConfirmDelete = async () => {
        if (!patientToDelete) return;

        try {
            setIsDeleting(true);
            await patientService.deletePatient(patientToDelete.id);
            toast({
                title: 'Succes',
                description: 'Pacientul a fost șters cu succes.',
            });
            refetch();
        } catch (error: any) {
            toast({
                title: 'Eroare',
                description: error.message || 'Nu s-a putut șterge pacientul',
                variant: 'destructive',
            });
        } finally {
            setIsDeleting(false);
            setPatientToDelete(null);
        }
    };

    const handleFormSubmit = async (newPatient: Patient, options: { closeDialog: boolean }) => {
        if (options.closeDialog) {
            setIsFormOpen(false);
            setIsWizardOpen(false);
            setSelectedPatient(null);
        } else {
            setSelectedPatient(newPatient);
            setIsFormOpen(true);
        }

        refetch();
    };

    const handleFiltersChange = (updatedFilters: PatientFilters) => {
        setFilters((prev) => {
            const next = { ...prev };
            Object.entries(updatedFilters).forEach(([key, value]) => {
                const typedKey = key as keyof PatientFilters;
                if (value === undefined || value === null || value === '') {
                    delete next[typedKey];
                } else {
                    next[typedKey] = value as any;
                }
            });
            // Always ensure this is true to show only premature patients
            next.is_premature = true;
            return next;
        });
        setCurrentPage(1);
    };

    const handleExport = async (format: 'csv' | 'excel' | 'pdf') => {
        try {
            setIsExporting(true);
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value));
                }
            });
            params.append('is_premature', 'true');
            params.append('format', format);

            const response = await apiClient.get('/api/patients/export', {
                params,
                responseType: 'blob',
            });

            const mimeType = format === 'pdf' ? 'application/pdf' : 'text/csv';
            const blob = new Blob([response.data], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const extension = format === 'pdf' ? 'pdf' : 'csv';
            link.href = url;
            link.download = `pacienti_prematuri_${new Date().toISOString().split('T')[0]}.${extension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast({
                title: 'Export realizat',
                description: 'Fișierul a fost generat cu succes.',
            });
        } catch (error: any) {
            toast({
                title: 'Eroare la export',
                description: error.message || 'Nu s-a putut genera fișierul',
                variant: 'destructive',
            });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <MainLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <Baby className="h-8 w-8 text-primary" />
                            Copii prematuri
                        </h1>
                        <p className="text-muted-foreground">
                            Gestionați pacienții cu date de prematuritate înregistrate
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" disabled={isExporting}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Exportă
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => handleExport('csv')}>
                                    Export CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleExport('excel')}>
                                    Export Excel (CSV)
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleExport('pdf')}>
                                    Export PDF
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {userRole === 'hospital' && (
                            <Button variant="outline" onClick={() => {
                                setTakeoverCnp('');
                                setIsTakeoverOpen(true);
                            }}>
                                <UserCheck className="mr-2 h-4 w-4" />
                                Preia pacient
                            </Button>
                        )}
                        <Button onClick={handleCreatePatient}>
                            <Plus className="mr-2 h-4 w-4" />
                            Adaugă pacient
                        </Button>
                    </div>
                </div>

                <Dialog open={isTakeoverOpen} onOpenChange={(open) => {
                    setIsTakeoverOpen(open);
                    if (!open) setTakeoverCnp('');
                }}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <UserCheck className="h-5 w-5" />
                                Preia pacient
                            </DialogTitle>
                            <DialogDescription>
                                Căutați pacientul după CNP pentru a-l prelua în gestiunea spitalului dumneavoastră.
                            </DialogDescription>
                        </DialogHeader>
                        <SearchPatientForTakeover
                            initialCnp={takeoverCnp}
                            onSuccess={() => {
                                setIsTakeoverOpen(false);
                                setTakeoverCnp('');
                                refetch();
                            }}
                        />
                    </DialogContent>
                </Dialog>

                {/* Patient List */}
                <PatientList
                    patients={patients?.data || []}
                    isLoading={isLoading}
                    error={error}
                    onEdit={handleEditPatient}
                    onDelete={handleDeletePatient}
                    userRole={userRole}
                    initialFilters={filters}
                    onFiltersChange={handleFiltersChange}
                    pagination={{
                        currentPage,
                        pageSize,
                        totalPages: patients?.pages || 1,
                        totalItems: patients?.total || 0,
                        onPageChange: setCurrentPage,
                    }}
                />

                {/* Patient Form Dialog */}
                <Dialog open={isFormOpen} onOpenChange={(open) => {
                    setIsFormOpen(open);
                    if (!open) setIsMaximized(false); // Reset maximize state when closing
                }}>
                    <DialogContent
                        hideClose
                        className={cn(
                            isMaximized
                                ? "w-screen h-screen max-w-none max-h-none overflow-y-auto flex flex-col"
                                : "w-[90vw] max-w-6xl max-h-[90vh] overflow-y-auto"
                        )}
                    >
                        <DialogHeader>
                            <div className="flex items-center justify-between">
                                <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                                    {selectedPatient ? (
                                        <>
                                            <Edit className="h-6 w-6" />
                                            Editează pacient
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="h-6 w-6" />
                                            Adaugă pacient nou
                                        </>
                                    )}
                                </DialogTitle>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setIsMaximized(!isMaximized)}
                                        className="h-8 w-8"
                                    >
                                        {isMaximized ? (
                                            <Minimize2 className="h-4 w-4" />
                                        ) : (
                                            <Maximize2 className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <DialogClose asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                        >
                                            <X className="h-4 w-4" />
                                            <span className="sr-only">Închide</span>
                                        </Button>
                                    </DialogClose>
                                </div>
                            </div>
                            <DialogDescription>
                                {selectedPatient
                                    ? 'Modificați informațiile pacientului existent'
                                    : 'Completați formularul pentru a înregistra un pacient nou'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className={cn("mt-6", isMaximized && "flex-1")}>
                            <PatientForm
                                patient={selectedPatient}
                                onSubmit={handleFormSubmit}
                                onCancel={() => setIsFormOpen(false)}
                            />
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Add Patient Wizard Dialog */}
                <Dialog open={isWizardOpen} onOpenChange={(open) => {
                    setIsWizardOpen(open);
                    if (!open) setIsWizardMaximized(false);
                }}>
                    <DialogContent
                        hideClose
                        className={cn(
                            isWizardMaximized
                                ? "w-screen h-screen max-w-none max-h-none overflow-y-auto flex flex-col"
                                : "w-[95vw] max-w-5xl max-h-[95vh] overflow-y-auto"
                        )}
                    >
                        <DialogHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <DialogTitle>Adăugare pacient nou</DialogTitle>
                                    <DialogDescription>
                                        Urmați pașii pentru a înregistra un nou pacient.
                                    </DialogDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setIsWizardMaximized(!isWizardMaximized)}
                                        className="h-8 w-8"
                                    >
                                        {isWizardMaximized ? (
                                            <Minimize2 className="h-4 w-4" />
                                        ) : (
                                            <Maximize2 className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <DialogClose asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                        >
                                            <X className="h-4 w-4" />
                                            <span className="sr-only">Închide</span>
                                        </Button>
                                    </DialogClose>
                                </div>
                            </div>
                        </DialogHeader>
                        <AddPatientWizard
                            onClose={() => setIsWizardOpen(false)}
                            onComplete={(patient) => handleFormSubmit(patient, { closeDialog: true })}
                            onTakeoverRequest={userRole === 'hospital' ? handleDuplicatePatient : undefined}
                        />
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={!!patientToDelete} onOpenChange={(open) => !open && setPatientToDelete(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Confirmare ștergere permanentă</DialogTitle>
                            <DialogDescription>
                                <div className="space-y-2">
                                    <p>
                                        Ești sigur că vrei să ștergi permanent pacientul{' '}
                                        <strong>{patientToDelete?.name}</strong>?
                                    </p>
                                    <p className="text-red-600 font-medium">
                                        ⚠️ Această acțiune este ireversibilă și va șterge definitiv toate datele asociate pacientului.
                                    </p>
                                </div>
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPatientToDelete(null)}>
                                Anulează
                            </Button>
                            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Șterge permanent
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};
